<template>
  <router-link
    v-if="variant !== 'parent'"
    :to="to"
    custom
    v-slot="{ isActive, isExactActive, navigate }"
  >
    <a
      :href="to || undefined"
      :class="[rowClasses, isActive ? activeClasses : inactiveClasses]"
      :aria-current="isExactActive ? 'page' : undefined"
      @click="onNavigate(navigate, $event)"
    >
      <i v-if="tablerIconClass" :class="[tablerIconClass, iconSizeClasses]" aria-hidden="true"></i>
      <span class="flex-1 min-w-0">{{ label }}</span>
    </a>
  </router-link>
  <CustomSimpleButton
    v-else
    :class="[rowClasses, inactiveClasses]"
    :aria-expanded="expanded"
    :aria-controls="panelId || undefined"
    @click="$emit('toggle')"
  >
    <i v-if="tablerIconClass" :class="[tablerIconClass, iconSizeClasses]" aria-hidden="true"></i>
    <span class="flex-1 min-w-0">{{ label }}</span>
    <i
      class="ti ti-chevron-down shrink-0 ml-auto [transition:transform_var(--motion-duration-normal)_var(--motion-ease-out)]"
      :class="{ 'transform-[rotate(180deg)]': expanded }"
      aria-hidden="true"
    ></i>
  </CustomSimpleButton>
</template>

<script lang="ts">
import CustomSimpleButton from "@/components/primitives/CustomSimpleButton.vue";
import { getTablerIconClass } from "@/methods/utils";
import { computed, defineComponent, PropType } from "vue";

type NavbarMenuItemVariant = "leaf" | "parent" | "child";

export default defineComponent({
  name: "NavbarMenuItem",
  components: { CustomSimpleButton },
  props: {
    variant: {
      type: String as PropType<NavbarMenuItemVariant>,
      required: true,
      validator: (value: string) => ["leaf", "parent", "child"].includes(value),
    },
    label: { type: String, required: true },
    to: { type: String, default: "" },
    icon: { type: String, default: "" },
    expanded: { type: Boolean, default: false },
    panelId: { type: String, default: "" },
  },
  emits: ["navigate", "toggle"],
  setup(props, { emit }) {
    const rowBaseClasses =
      "flex items-center w-full py-3 " +
      "text-left no-underline cursor-pointer " +
      "[transition:background-color_var(--motion-duration-fast)_var(--motion-ease-in-out)] " +
      "hover:bg-color-primary " +
      "focus-visible:[outline:1px_solid_var(--color-primary)] focus-visible:[outline-offset:-1px]";
    const rowClasses = computed(() =>
      props.variant === "child"
        ? `${rowBaseClasses} ps-8 pe-3 l:pe-4 text-xs`
        : `${rowBaseClasses} px-3 l:px-4 text-s`,
    );
    const inactiveClasses = "color-primary bg-transparent";
    const activeClasses = "font-semibold color-tertiary bg-color-primary";
    const tablerIconClass = computed(() => getTablerIconClass(props.icon));
    const iconSizeClasses = computed(() =>
      props.variant === "child" ? "me-2 shrink-0 text-m" : "me-2 shrink-0 text-l l:text-m",
    );

    function onNavigate(navigate: (event?: MouseEvent) => void, event: MouseEvent): void {
      navigate(event);
      emit("navigate");
    }

    return {
      rowClasses,
      inactiveClasses,
      activeClasses,
      tablerIconClass,
      iconSizeClasses,
      onNavigate,
    };
  },
});
</script>
